const initNativeScroll = (viewportNode, options = {}) => {
  var _a, _b;
  const callbacks = {
    init: [],
    select: [],
    reInit: [],
    destroy: [],
    resize: []
  };
  const fire = (evt) => {
    if (callbacks[evt]) {
      callbacks[evt].forEach((cb) => cb(emblaApi));
    }
  };
  const initialSlideCount = ((_a = viewportNode.firstElementChild) == null ? void 0 : _a.children.length) || 0;
  const requestedStartIndex = Math.min(
    Math.max(0, Number(options.startIndex) || 0),
    Math.max(0, initialSlideCount - 1)
  );
  let activeIndex = requestedStartIndex;
  let previousIndex = requestedStartIndex;
  const intersectingSlides = /* @__PURE__ */ new Set();
  const align = options.align || "start";
  const loop = options.loop === true;
  const isRtl = viewportNode.matches(":dir(rtl)");
  const getSlides = () => {
    const container = viewportNode.firstElementChild;
    return container ? Array.from(container.children) : [];
  };
  const shouldReduceMotion = () => {
    var _a2;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches || ((_a2 = document.body) == null ? void 0 : _a2.dataset.reduceMotion) === "true";
  };
  const getSnapCount = () => {
    const slides = getSlides();
    if (slides.length <= 1) return slides.length;
    if (align === "center") return slides.length;
    let spv = 1;
    const rootComponent = viewportNode.closest("[data-items-per-row-mobile]");
    if (rootComponent) {
      const spvStr = rootComponent.getAttribute("data-items-per-row-mobile");
      if (spvStr) spv = parseFloat(spvStr) || 1;
    } else {
      const emblaNode = viewportNode.closest(".embla");
      if (emblaNode) {
        const spvStr = window.getComputedStyle(emblaNode).getPropertyValue("--slide-per-view-mobile").trim();
        if (spvStr) spv = parseFloat(spvStr) || 1;
      }
    }
    return Math.max(1, slides.length - Math.floor(spv) + 1);
  };
  const emblaApi = {
    on: (evt, cb) => {
      if (callbacks[evt]) callbacks[evt].push(cb);
      return emblaApi;
    },
    off: (evt, cb) => {
      if (callbacks[evt])
        callbacks[evt] = callbacks[evt].filter((c) => c !== cb);
      return emblaApi;
    },
    scrollPrev: () => {
      const slides = getSlides();
      const wraps = loop && slides.length > 1 && activeIndex === 0;
      emblaApi.scrollTo(
        wraps ? slides.length - 1 : Math.max(0, activeIndex - 1),
        wraps
      );
    },
    scrollNext: () => {
      const slides = getSlides();
      const wraps = loop && slides.length > 1 && activeIndex === slides.length - 1;
      emblaApi.scrollTo(
        wraps ? 0 : Math.min(slides.length - 1, activeIndex + 1),
        wraps
      );
    },
    scrollTo: (index, jump = false) => {
      const slides = getSlides();
      const slide = slides[index];
      if (!slide) return;
      const viewportRect = viewportNode.getBoundingClientRect();
      const slideRect = slide.getBoundingClientRect();
      const delta = align === "center" ? slideRect.left + slideRect.width / 2 - (viewportRect.left + viewportRect.width / 2) : isRtl ? slideRect.right - viewportRect.right : slideRect.left - viewportRect.left;
      if (jump && index !== activeIndex) {
        previousIndex = activeIndex;
        activeIndex = index;
        fire("select");
      }
      if (Math.abs(delta) < 1) return;
      viewportNode.scrollBy({
        left: delta,
        behavior: jump || shouldReduceMotion() ? "instant" : "smooth"
      });
    },
    canScrollPrev: () => loop && getSnapCount() > 1 || activeIndex > 0,
    canScrollNext: () => loop && getSnapCount() > 1 || activeIndex < getSnapCount() - 1,
    scrollSnapList: () => {
      const snapCount = getSnapCount();
      return Array(snapCount).fill(0).map((_, i) => i);
    },
    selectedScrollSnap: () => activeIndex,
    previousScrollSnap: () => previousIndex,
    plugins: () => ({}),
    internalEngine: () => ({ options: { active: true, loop } }),
    destroy: () => {
      var _a2;
      observer.disconnect();
      (_a2 = viewportNode.closest(".embla")) == null ? void 0 : _a2.classList.remove("is-native-scroll");
    },
    reInit: () => {
      observer.disconnect();
      intersectingSlides.clear();
      getSlides().forEach((s) => observer.observe(s));
      fire("reInit");
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (viewportNode.clientWidth > 0) {
            fire("select");
          }
        });
      });
    }
  };
  const observer = new IntersectionObserver(
    (entries) => {
      let changed = false;
      entries.forEach((entry) => {
        const slides = getSlides();
        const index = slides.indexOf(entry.target);
        if (index === -1) return;
        if (entry.isIntersecting) {
          if (!intersectingSlides.has(index)) {
            intersectingSlides.add(index);
            changed = true;
          }
        } else {
          if (intersectingSlides.has(index)) {
            intersectingSlides.delete(index);
            changed = true;
          }
        }
      });
      if (changed && intersectingSlides.size > 0) {
        let targetIndex;
        if (align === "center") {
          targetIndex = Array.from(intersectingSlides)[0];
        } else {
          targetIndex = Math.min(...Array.from(intersectingSlides));
        }
        const maxIndex = getSnapCount() - 1;
        targetIndex = Math.min(targetIndex, maxIndex);
        if (targetIndex !== activeIndex) {
          previousIndex = activeIndex;
          activeIndex = targetIndex;
          fire("select");
        }
      }
    },
    {
      root: viewportNode,
      // Start alignment tracks 50% slide visibility.
      // Center alignment creates a 2% wide vertical detection band at the exact center of the screen.
      rootMargin: align === "center" ? "0px -49% 0px -49%" : "0px",
      threshold: align === "center" ? 0 : 0.5
    }
  );
  getSlides().forEach((s) => observer.observe(s));
  emblaApi.scrollTo(requestedStartIndex, true);
  (_b = viewportNode.closest(".embla")) == null ? void 0 : _b.classList.add("is-native-scroll");
  setTimeout(() => {
    fire("init");
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (viewportNode.clientWidth > 0) {
          emblaApi.scrollTo(requestedStartIndex, true);
          fire("select");
        }
      });
    });
  }, 0);
  return emblaApi;
};
export {
  initNativeScroll
};
